import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Slide {
  id: string;
  tag: string;
  title: string;
  subtitle: string;
  desc: string;
  image: string;
  primaryTo: string;
  primaryLabel: string;
  secondaryTo: string;
}

const slides: Slide[] = [
  {
    id: 's1',
    tag: '本剧热播',
    title: '迷人的保姆',
    subtitle: 'The Charming Nanny',
    desc: '她走进豪门之家，用温柔与智慧化解层层矛盾，也揭开了一段被掩埋多年的往事。每周五、周六更新。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20cinematic%20interior%20of%20an%20upscale%20modern%20apartment%20with%20large%20windows%2C%20golden%20afternoon%20sunlight%20streaming%20across%20soft%20linen%20sofa%20and%20potted%20plants%2C%20muted%20amber%20and%20deep%20navy%20tones%2C%20shallow%20depth%20of%20field%2C%20moody%20drama%20still%2C%20no%20text&width=1920&height=1080&seq=nanny-hero-bg-01&orientation=landscape',
    primaryTo: '/play/nanny-18',
    primaryLabel: '观看最新一集',
    secondaryTo: '/detail/nanny',
  },
  {
    id: 's2',
    tag: '治愈新作',
    title: '深夜家政',
    subtitle: 'Midnight Housekeeping',
    desc: '每当城市入睡，她的夜晚才刚开始。一次次深夜上门，遇见一个个需要被倾听的人。',
    image:
      'https://readdy.ai/api/search-image?query=Cozy%20apartment%20kitchen%20at%20midnight%2C%20single%20warm%20lamp%20glow%2C%20deep%20blue%20shadows%20and%20amber%20highlights%2C%20quiet%20healing%20drama%20atmosphere%2C%20soft%20textures%2C%20no%20text&width=1920&height=1080&seq=midnight-bd-04&orientation=landscape',
    primaryTo: '/play/midnight-1',
    primaryLabel: '立即观看',
    secondaryTo: '/detail/midnight',
  },
  {
    id: 's3',
    tag: '青春追梦',
    title: '逆光而来',
    subtitle: 'Against the Light',
    desc: '她迎着光走来，也走进自己的梦想里。一群年轻人追逐舞台梦想的温柔叙事。',
    image:
      'https://readdy.ai/api/search-image?query=Warm%20backlit%20empty%20dance%20studio%20with%20sunlight%20streaming%20through%20tall%20windows%2C%20dust%20motes%20in%20the%20air%2C%20amber%20glow%20and%20navy%20shadows%2C%20cinematic%20youth%20drama%20still%2C%20no%20text&width=1920&height=1080&seq=backlight-bd-07&orientation=landscape',
    primaryTo: '/play/backlight-1',
    primaryLabel: '立即观看',
    secondaryTo: '/detail/backlight',
  },
];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title}宣传图`}
            title={`${slide.title} - 迷人的保姆在线观看`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/55 to-primary-950/25"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 lg:px-12">
        <div className="h-full flex flex-col justify-center max-w-2xl">
          <span className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-accent-500/90 text-background-50 dark:text-foreground-950 text-xs font-medium mb-5 animate-fade-up">
            <i className="ri-fire-fill"></i>
            {slides[index].tag}
          </span>
          <h2
            key={slides[index].id}
            className="text-3xl md:text-5xl font-bold text-background-50 leading-tight animate-fade-up"
          >
            {slides[index].title}
          </h2>
          <p className="mt-2 font-label text-sm md:text-base text-background-300 tracking-wide animate-fade-up">
            {slides[index].subtitle}
          </p>
          <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-xl animate-fade-up">
            {slides[index].desc}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={slides[index].primaryTo}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              {slides[index].primaryLabel}
            </Link>
            <Link
              to={slides[index].secondaryTo}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-information-line text-lg"></i>
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-background-50/15 border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors backdrop-blur-sm"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-background-50/15 border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/30 transition-colors backdrop-blur-sm"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到${slide.title}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-accent-500' : 'w-2.5 bg-background-50/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
}